import React, { useState, useEffect } from 'react'
import { Button, TextField, MenuItem, Select, InputLabel, FormControl, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Box, Checkbox, ListItemText, Chip, FormControlLabel, Switch } from '@mui/material'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/store'
import { Department } from 'src/features/departmentsSlice'
import { createPosition } from 'src/features/positionsSlice'
import RoleChipSelect from './RoleChipSelect'
import SkillsSection from './SkillsSection'
import PositionPointDistribution, { PositionDistributionValue } from './PositionPointDistribution'
import { countries } from 'src/constants/countries'
import { countryCodeMap, getCountryFilterBy } from 'src/constants/countryCodes'
import { seniorityLevels, getSeniorityLevelLabel } from 'src/constants/seniorityLevels'
import { NumericFormatCustom } from '../shared/NumericFormatCustom'
import CreateOptionSelect from '../shared/CreateOptionSelect'
import RichTextField from '../shared/RichTextField'
import CircleIcon from '@mui/icons-material/Circle'
import axios from 'axios'
import { API_URL } from 'src/config/api'
import { useTranslation } from 'react-i18next'
import i18n from 'src/i18n'
import { normalizeApiLanguage } from 'src/utils/language'
import { dedupeSkills } from './skillUtils'

interface Skill {
  name: string
  label?: string
  matchScore: number
}

interface SelectableItem {
  key: string
  text: string
}

const desiredAttributesSchema = Yup.array()
  .of(
    Yup.object({
      key: Yup.string().required(),
      percentage: Yup.number().required().min(0).max(100)
    })
  )
  .test('total-100', 'Total points must be exactly 100', (value) => {
    const total = (value || []).reduce((sum, item) => sum + (item?.percentage || 0), 0)
    return total === 100
  })
  .required('Please distribute points')

const getSelectableKey = (options: SelectableItem[], value: string): string => {
  if (!value) return ''
  const byKey = options.find((option) => option.key === value)
  if (byKey) return byKey.key
  const byText = options.find((option) => option.text === value)
  if (byText) return byText.key
  const normalizedValue = value.trim().toLowerCase()
  const byTextInsensitive = options.find((option) => option.text.trim().toLowerCase() === normalizedValue)
  return byTextInsensitive ? byTextInsensitive.key : value
}

const getSelectableLabel = (options: SelectableItem[], value: string): string => {
  if (!value) return ''
  return options.find((option) => option.key === value)?.text || value
}

const schema = Yup.object({
  jobTitle: Yup.string().required('Job title is required'),
  departmentId: Yup.string().required('Department is required'),
  unit: Yup.string(),
  team: Yup.string(),
  seniorityLevel: Yup.string().required('Seniority level is required'),
  workType: Yup.string().required('Work type is required'),
  employeeLocation: Yup.string().required('Employee location is required'),
  city: Yup.string().required('City is required'),
  state: Yup.string().notRequired(),
  district: Yup.string().notRequired(),
  employmentType: Yup.string().required('Employment type is required'),
  minSalary: Yup.string().required('Minimum salary is required'),
  maxSalary: Yup.string().required('Maximum salary is required'),
  currency: Yup.string().required('Currency is required'),
  showSalaryToTalent: Yup.boolean().notRequired(),
  visaSponsorship: Yup.string().required('Visa sponsorship is required'),
  urgentHiring: Yup.string().required('Urgent Hiring selection is required'),
  employmentScope: Yup.string().required('Employment scope is required'),
  skills: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required('Skill name is required'),
        matchScore: Yup.number().required('Match score is required').min(0, 'Match score must be at least 0').max(100, "Match score can't be more than 100")
      })
    )
    .min(1, 'At least one skill is required'),
  aboutUs: Yup.string().required('About Us is required'),
  teamResponsibilities: Yup.string().required('Team & Responsibilities is required'),
  requirements: Yup.string().required('Requirements is required'),
  desiredAttributes1: desiredAttributesSchema,
  desiredAttributes2: desiredAttributesSchema,
  whatWeOffer: Yup.string().notRequired(),
  status: Yup.string().required('Status is required'),
  benefits: Yup.array().notRequired(),
  education: Yup.array().notRequired(),
  physicalConditions: Yup.array().notRequired(),
  workSchedule: Yup.string().notRequired()
})

// Infer type from Yup schema to ensure perfect type alignment
type JobFormInputs = Yup.InferType<typeof schema>

// Skills are now fetched from API (selectables.skills)

const JobCreatePage = ({ handleClose, open, departmentId }: { open: boolean; handleClose: (open: boolean) => void; departmentId?: string }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { departments, loading } = useSelector((state: RootState) => state.departments)

  const { t } = useTranslation()
  const currentLanguage = normalizeApiLanguage(i18n.resolvedLanguage || i18n.language)
  const { token } = useSelector((state: RootState) => state.auth)
  const defaultTextValues = React.useMemo(
    () => ({
      aboutUs: t('positions.form.defaultAboutUs'),
      teamResponsibilities: t('positions.form.defaultTeamResponsibilities'),
      requirements: t('positions.form.defaultRequirements')
    }),
    [t]
  )

  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([])
  const [allSkills, setAllSkills] = useState<Skill[]>([])
  const [physicalConditionsOptions, setPhysicalConditionsOptions] = useState<SelectableItem[]>([])
  const [jobTitleOptions, setJobTitleOptions] = useState<{ title: string }[]>([])
  const [countryOptions, setCountryOptions] = useState<SelectableItem[]>([])
  const [workingMethodOptions, setWorkingMethodOptions] = useState<SelectableItem[]>([])
  const [typeOfPositionOptions, setTypeOfPositionOptions] = useState<SelectableItem[]>([])
  const [employmentScopeOptions, setEmploymentScopeOptions] = useState<SelectableItem[]>([])
  const [hiringUrgencyOptions, setHiringUrgencyOptions] = useState<SelectableItem[]>([])
  const [workHoursArrangementOptions, setWorkHoursArrangementOptions] = useState<SelectableItem[]>([])
  const [benefitsOptions, setBenefitsOptions] = useState<SelectableItem[]>([])
  const [trainingOpportunitiesOptions, setTrainingOpportunitiesOptions] = useState<SelectableItem[]>([])
  const [desiredAttributes1Options, setDesiredAttributes1Options] = useState<SelectableItem[]>([])
  const [desiredAttributes2Options, setDesiredAttributes2Options] = useState<SelectableItem[]>([])
  const [cityOptions, setCityOptions] = useState<{ title: string }[]>([])

  // Find the department object if departmentId is provided
  const selectedDepartment = departmentId ? departments.find((d) => d.id === departmentId) : null

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    getValues,
    watch,
    trigger
  } = useForm<JobFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      jobTitle: '',
      departmentId: selectedDepartment?.id || '',
      unit: '',
      team: '',
      seniorityLevel: '',
      workType: '',
      employeeLocation: '',
      city: '',
      state: '',
      district: '',
      employmentType: '',
      currency: '',
      showSalaryToTalent: true,
      visaSponsorship: '',
      skills: [],
      urgentHiring: '',
      employmentScope: '',
      aboutUs: defaultTextValues.aboutUs,
      teamResponsibilities: defaultTextValues.teamResponsibilities,
      requirements: defaultTextValues.requirements,
      desiredAttributes1: [],
      desiredAttributes2: [],
      whatWeOffer: '',
      status: 'Draft',
      benefits: [],
      education: [],
      physicalConditions: [],
      workSchedule: ''
    }
  })
  const previousDefaultTextValuesRef = React.useRef(defaultTextValues)

  // Pre-fill department when selectedDepartment changes
  useEffect(() => {
    if (selectedDepartment) {
      setValue('departmentId', selectedDepartment.id)
    }
  }, [selectedDepartment, setValue])

  useEffect(() => {
    const previousDefaults = previousDefaultTextValuesRef.current

    ;(['aboutUs', 'teamResponsibilities', 'requirements'] as const).forEach((fieldName) => {
      const currentValue = getValues(fieldName)
      const shouldReplace =
        !currentValue ||
        currentValue === previousDefaults[fieldName] ||
        currentValue === defaultTextValues[fieldName]

      if (shouldReplace) {
        setValue(fieldName, defaultTextValues[fieldName], {
          shouldDirty: false,
          shouldValidate: false
        })
      }
    })

    previousDefaultTextValuesRef.current = defaultTextValues
  }, [defaultTextValues, getValues, setValue])

  // Fetch jobTitles from MongoDB with debounced search
  const handleJobTitleInputChange = async (value: string) => {
    if (value.length > 0) {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzySearch`,
          {
            name: 'jobTitles',
            statement: value
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert string array to { title: string } format
        setJobTitleOptions(response.data.map((title: string) => ({ title })))
      } catch (error) {
        console.error('Failed to fetch jobTitles:', error)
        // Fallback to static options if API fails
        setJobTitleOptions([
          { title: 'Frontend Developer' },
          { title: 'Backend Developer' },
          { title: 'Full Stack Developer' },
          { title: 'DevOps Engineer' },
          { title: 'Mobile Developer' },
          { title: 'QA Engineer' },
          { title: 'Data Engineer' },
          { title: 'ML Engineer' },
          { title: 'Product Manager' },
          { title: 'UI/UX Designer' },
          { title: 'Business Analyst' },
          { title: 'Project Manager' }
        ])
      }
    }
    // Don't clear options when input is empty - let the user see all options
  }

  // Debounced version of handleJobTitleInputChange
  const [debouncedTimeout, setDebouncedTimeout] = React.useState<NodeJS.Timeout | null>(null)

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debouncedTimeout) {
        clearTimeout(debouncedTimeout)
      }
    }
  }, [debouncedTimeout])

  const handleJobTitleInputDebounced = (value: string) => {
    if (debouncedTimeout) {
      clearTimeout(debouncedTimeout)
    }
    const newTimeout = setTimeout(() => {
      handleJobTitleInputChange(value)
    }, 300)
    setDebouncedTimeout(newTimeout)
  }

  // Fetch countries from MongoDB
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'countries',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setCountryOptions(items)
      } catch (error) {
        console.error('Failed to fetch countries:', error)
        setCountryOptions(
          countries.map((country) => ({
            key: countryCodeMap[country] || country,
            text: country
          }))
        )
      }
    }
    if (token && open) {
      fetchCountries()
    }
  }, [token, open, currentLanguage])

  // PhysicalConditions - use static list (API with empty statement causes 500 error)
  useEffect(() => {
    setPhysicalConditionsOptions([
      { key: 'centralofficelocation', text: t('positions.physicalConditionsOptions.centralofficelocation') },
      { key: 'freshcoffeeintheoffice', text: t('positions.physicalConditionsOptions.freshcoffeeintheoffice') },
      { key: 'workspacewithaview', text: t('positions.physicalConditionsOptions.workspacewithaview') },
      { key: 'comfortableseating', text: t('positions.physicalConditionsOptions.comfortableseating') },
      { key: 'latestversionequipment', text: t('positions.physicalConditionsOptions.latestversionequipment') }
    ])
  }, [])

  // Fetch workingMethods from MongoDB
  useEffect(() => {
    const fetchWorkingMethods = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'workingMethods',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert Item array to SelectableItem format
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setWorkingMethodOptions(items)
      } catch (error) {
        console.error('Failed to fetch workingMethods:', error)
        // Fallback to static options if API fails
        setWorkingMethodOptions([
          { key: 'onsite', text: 'On-site' },
          { key: 'remote', text: 'Remote' },
          { key: 'hybrid', text: 'Hybrid' }
        ])
      }
    }
    if (token && open) {
      fetchWorkingMethods()
    }
  }, [token, open, currentLanguage])

  // Fetch typeOfPositions from MongoDB
  useEffect(() => {
    const fetchTypeOfPositions = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'typeOfPositions',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert Item array to SelectableItem format
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setTypeOfPositionOptions(items)
      } catch (error) {
        console.error('Failed to fetch typeOfPositions:', error)
        // Fallback to static options if API fails
        setTypeOfPositionOptions([
          { key: 'fulltime', text: 'Full-time' },
          { key: 'parttime', text: 'Part-time' },
          { key: 'contractor', text: 'Contractor' },
          { key: 'temporary', text: 'Temporary' }
        ])
      }
    }
    if (token && open) {
      fetchTypeOfPositions()
    }
  }, [token, open])

  // Fetch employmentScopes from MongoDB
  useEffect(() => {
    const fetchEmploymentScopes = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'employmentScopes',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert Item array to SelectableItem format
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setEmploymentScopeOptions(items)
      } catch (error) {
        console.error('Failed to fetch employmentScopes:', error)
        // Fallback to static options if API fails
        setEmploymentScopeOptions([
          { key: 'internal', text: 'Internal' },
          { key: 'external', text: 'External' },
          { key: 'internalandexternal', text: 'Internal and External' }
        ])
      }
    }
    if (token && open) {
      fetchEmploymentScopes()
    }
  }, [token, open])

  // Fetch hiringUrgencies from MongoDB
  useEffect(() => {
    const fetchHiringUrgencies = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'hiringUrgencies',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setHiringUrgencyOptions(items)
      } catch (error) {
        console.error('Failed to fetch hiringUrgencies:', error)
        setHiringUrgencyOptions([
          { key: 'veryurgent', text: 'Very urgent' },
          { key: 'norush', text: 'No rush, but we need good candidates' },
          { key: 'always', text: 'Always open to hiring great talent' }
        ])
      }
    }
    if (token && open) {
      fetchHiringUrgencies()
    }
  }, [token, open])

  // Fetch workhoursArrangements from MongoDB
  useEffect(() => {
    const fetchWorkHoursArrangements = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'workhoursArrangements',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setWorkHoursArrangementOptions(items)
      } catch (error) {
        console.error('Failed to fetch workhoursArrangements:', error)
        setWorkHoursArrangementOptions([
          { key: 'regular', text: t('positions.workingHoursArrangement.regular') },
          { key: 'flexible', text: t('positions.workingHoursArrangement.flexible') },
          { key: 'intensive', text: t('positions.workingHoursArrangement.intensive') },
          { key: 'breakfocused', text: t('positions.workingHoursArrangement.breakfocused') }
        ])
      }
    }
    if (token && open) {
      fetchWorkHoursArrangements()
    }
  }, [token, open, t])

  // Fetch benefits from MongoDB
  useEffect(() => {
    const fetchBenefits = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'benefits',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setBenefitsOptions(items)
      } catch (error) {
        console.error('Failed to fetch benefits:', error)
        setBenefitsOptions([
          { key: 'performancebonus', text: t('positions.benefitsOptions.performancebonus') },
          { key: 'stockoptions', text: t('positions.benefitsOptions.stockoptions') },
          { key: 'privatehealthinsurance', text: t('positions.benefitsOptions.privatehealthinsurance') },
          { key: 'educationsupport', text: t('positions.benefitsOptions.educationsupport') },
          { key: 'overtimepay', text: t('positions.benefitsOptions.overtimepay') },
          { key: 'employeesupportservices', text: t('positions.benefitsOptions.employeesupportservices') },
          { key: 'extendedannualleave', text: t('positions.benefitsOptions.extendedannualleave') },
          { key: 'transportationassistance', text: t('positions.benefitsOptions.transportationassistance') },
          { key: 'parentalleave', text: t('positions.benefitsOptions.parentalleave') },
          { key: 'remoteworksupportbudget', text: t('positions.benefitsOptions.remoteworksupportbudget') }
        ])
      }
    }
    if (token && open) {
      fetchBenefits()
    }
  }, [token, open, t])

  // Fetch trainingOpportunities from MongoDB
  useEffect(() => {
    const fetchTrainingOpportunities = async () => {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'trainingOpportunities',
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        const items = response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
        setTrainingOpportunitiesOptions(items)
      } catch (error) {
        console.error('Failed to fetch trainingOpportunities:', error)
        setTrainingOpportunitiesOptions([
          { key: 'incompanytraining', text: t('positions.trainingOpportunitiesOptions.incompanytraining') },
          { key: 'personaldevelopmenttraining', text: t('positions.trainingOpportunitiesOptions.personaldevelopmenttraining') },
          { key: 'leadershiptraining', text: t('positions.trainingOpportunitiesOptions.leadershiptraining') },
          { key: 'certificationsupport', text: t('positions.trainingOpportunitiesOptions.certificationsupport') },
          { key: 'masterandphdleavesupport', text: t('positions.trainingOpportunitiesOptions.masterandphdleavesupport') },
          { key: 'masterandphdtuitionsupport', text: t('positions.trainingOpportunitiesOptions.masterandphdtuitionsupport') }
        ])
      }
    }
    if (token && open) {
      fetchTrainingOpportunities()
    }
  }, [token, open, t])

  // Fetch desiredAttributes1 and desiredAttributes2 from MongoDB
  useEffect(() => {
    const fetchDesiredAttributes = async () => {
      const fetchOne = async (name: string) => {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name,
            statement: '',
            for: 'company',
            language: currentLanguage
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )

        return response.data.map((item: { key: string; text: string }) => ({
          key: item.key,
          text: item.text
        }))
      }

      try {
        const [attributes1, attributes2] = await Promise.all([
          fetchOne('desiredAttributes1'),
          fetchOne('desiredAttributes2')
        ])
        setDesiredAttributes1Options(attributes1)
        setDesiredAttributes2Options(attributes2)
      } catch (error) {
        console.error('Failed to fetch desiredAttributes:', error)
        setDesiredAttributes1Options([])
        setDesiredAttributes2Options([])
      }
    }

    if (token && open) {
      fetchDesiredAttributes()
    }
  }, [token, open, currentLanguage])

  // Fetch skills from MongoDB with debounced search
  const handleSkillSearch = async (value: string) => {
    if (value.length > 0) {
      try {
        const response = await axios.post(
          `${API_URL}/fuzzy/fuzzyWithItemSearch`,
          {
            name: 'skills',
            statement: value
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )
        // Convert Item array to Skill format with default matchScore
        const skills = response.data.map((item: { key: string; text: string }) => ({
          name: item.key,
          label: item.text,
          matchScore: 0 // Default matchScore, will be recalculated based on jobTitle if needed
        }))
        setAllSkills(dedupeSkills(skills))
      } catch (error) {
        console.error('Failed to fetch skills:', error)
        // Fallback to empty array if API fails
        setAllSkills([])
      }
    }
  }

  // Debounced version of handleSkillSearch
  const [skillSearchDebounce, setSkillSearchDebounce] = React.useState<NodeJS.Timeout | null>(null)

  // Cleanup debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (skillSearchDebounce) {
        clearTimeout(skillSearchDebounce)
      }
    }
  }, [skillSearchDebounce])

  const handleSkillSearchDebounced = (value: string) => {
    if (skillSearchDebounce) {
      clearTimeout(skillSearchDebounce)
    }
    const newTimeout = setTimeout(() => {
      handleSkillSearch(value)
    }, 300)
    setSkillSearchDebounce(newTimeout)
  }

  // NOTE: Departments are already fetched by parent (Positions.tsx)
  // No need to fetch again here

  const handleAddSkill = (newSkills: Skill[]) => {
    const uniqueSkills = dedupeSkills(newSkills)

    setSelectedSkills(uniqueSkills)
    setValue('skills', uniqueSkills)
    trigger('skills')
  }

  const watchedEmployeeLocation = watch('employeeLocation')
  const watchedCity = watch('city')
  const watchedState = watch('state')
  const watchedJobTitle = watch('jobTitle')
  const watchedBenefits = watch('benefits')
  const watchedEducation = watch('education')
  const watchedDesiredAttributes1 = watch('desiredAttributes1')
  const watchedDesiredAttributes2 = watch('desiredAttributes2')

  const normalizeDesiredAttributes = (
    options: SelectableItem[],
    values?: PositionDistributionValue[]
  ): PositionDistributionValue[] => {
    return options.map((option) => ({
      key: option.key,
      percentage: Math.max(
        0,
        Math.min(
          100,
          Math.round(values?.find((item) => item.key === option.key)?.percentage ?? 0)
        )
      )
    }))
  }

  const prevEmployeeLocationRef = React.useRef<string | null>(null)
  const [citySearchDebounce, setCitySearchDebounce] = React.useState<NodeJS.Timeout | null>(null)
  const jobTitleSkillsDebounceRef = React.useRef<NodeJS.Timeout | null>(null)

  // Reset selected skills when job title changes
  useEffect(() => {
    setSelectedSkills([])
    setValue('skills', [])
  }, [setValue, watchedJobTitle])

  // Cleanup city debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (citySearchDebounce) {
        clearTimeout(citySearchDebounce)
      }
    }
  }, [citySearchDebounce])

  // Cleanup job title skills debounce timeout on unmount
  React.useEffect(() => {
    return () => {
      if (jobTitleSkillsDebounceRef.current) {
        clearTimeout(jobTitleSkillsDebounceRef.current)
      }
    }
  }, [])

  const fetchCities = async (value: string) => {
    const filterBy = getCountryFilterBy(watchedEmployeeLocation)
    try {
      const response = await axios.post(
        `${API_URL}/fuzzy/fuzzyWithItemSearch`,
        {
          name: 'cities',
          statement: value,
          for: 'company',
          language: currentLanguage,
          filterBy
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const items = response.data.map((item: { key: string; text: string }) => ({
        title: item.text
      }))
      setCityOptions(items)
    } catch (error) {
      console.error('Failed to fetch cities:', error)
      setCityOptions([])
    }
  }

  const handleCityInputDebounced = (value: string) => {
    if (!token || !open || !watchedEmployeeLocation) return
    if (citySearchDebounce) {
      clearTimeout(citySearchDebounce)
    }
    const newTimeout = setTimeout(() => {
      fetchCities(value)
    }, 300)
    setCitySearchDebounce(newTimeout)
  }

  useEffect(() => {
    if (!token || !open) return
    if (prevEmployeeLocationRef.current === null) {
      prevEmployeeLocationRef.current = watchedEmployeeLocation || ''
      if (watchedEmployeeLocation) {
        fetchCities('')
      }
      return
    }
    if (prevEmployeeLocationRef.current !== watchedEmployeeLocation) {
      prevEmployeeLocationRef.current = watchedEmployeeLocation || ''
      setValue('city', '')
      setValue('state', '')
      setValue('district', '')
      setCityOptions([])
      if (watchedEmployeeLocation) {
        fetchCities('')
      }
    }
  }, [token, open, watchedEmployeeLocation, setValue])

  const fetchSkillsForJobTitle = async (value: string) => {
    if (!value.trim()) {
      setAllSkills([])
      return
    }
    try {
      const response = await axios.post(
        `${API_URL}/fuzzy/fuzzyWithItemSearch`,
        {
          name: 'skills',
          statement: value,
          for: 'company',
          language: currentLanguage,
          filterBy: ''
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )
      const skills = response.data.map((item: { key: string; text: string }) => ({
        name: item.key,
        label: item.text,
        matchScore: 0
      }))
      setAllSkills(dedupeSkills(skills))
    } catch (error) {
      console.error('Failed to fetch skills for job title:', error)
    }
  }

  useEffect(() => {
    if (!token || !open) return
    if (!watchedJobTitle || watchedJobTitle.trim().length < 2) {
      setAllSkills([])
      return
    }
    if (jobTitleSkillsDebounceRef.current) {
      clearTimeout(jobTitleSkillsDebounceRef.current)
    }
    const newTimeout = setTimeout(() => {
      fetchSkillsForJobTitle(watchedJobTitle)
    }, 300)
    jobTitleSkillsDebounceRef.current = newTimeout
  }, [token, open, watchedJobTitle])

  useEffect(() => {
    if (!benefitsOptions.length || !watchedBenefits?.length) return
    const normalized = watchedBenefits.map((value) => getSelectableKey(benefitsOptions, value))
    if (JSON.stringify(normalized) !== JSON.stringify(watchedBenefits)) {
      setValue('benefits', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [benefitsOptions, watchedBenefits, setValue])

  useEffect(() => {
    if (!trainingOpportunitiesOptions.length || !watchedEducation?.length) return
    const normalized = watchedEducation.map((value) => getSelectableKey(trainingOpportunitiesOptions, value))
    if (JSON.stringify(normalized) !== JSON.stringify(watchedEducation)) {
      setValue('education', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [trainingOpportunitiesOptions, watchedEducation, setValue])

  useEffect(() => {
    if (!desiredAttributes1Options.length) return
    const normalized = normalizeDesiredAttributes(desiredAttributes1Options, watchedDesiredAttributes1 as PositionDistributionValue[] | undefined)
    if (JSON.stringify(normalized) !== JSON.stringify(watchedDesiredAttributes1 || [])) {
      setValue('desiredAttributes1', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [desiredAttributes1Options, watchedDesiredAttributes1, setValue])

  useEffect(() => {
    if (!desiredAttributes2Options.length) return
    const normalized = normalizeDesiredAttributes(desiredAttributes2Options, watchedDesiredAttributes2 as PositionDistributionValue[] | undefined)
    if (JSON.stringify(normalized) !== JSON.stringify(watchedDesiredAttributes2 || [])) {
      setValue('desiredAttributes2', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [desiredAttributes2Options, watchedDesiredAttributes2, setValue])

  useEffect(() => {
    if (!countryOptions.length || !watchedEmployeeLocation) return
    const normalized = getSelectableKey(countryOptions, watchedEmployeeLocation)
    if (normalized !== watchedEmployeeLocation) {
      setValue('employeeLocation', normalized, { shouldDirty: false, shouldValidate: false })
    }
  }, [countryOptions, watchedEmployeeLocation, setValue])

  const onSubmit: SubmitHandler<JobFormInputs> = (data) => {
    if (data.skills && data.departmentId) {
      const normalizedEmploymentType = getSelectableKey(typeOfPositionOptions, data.employmentType)
      const normalizedWorkType = getSelectableKey(workingMethodOptions, data.workType)
      const normalizedEmploymentScope = getSelectableKey(employmentScopeOptions, data.employmentScope)
      const normalizedUrgentHiring = getSelectableKey(hiringUrgencyOptions, data.urgentHiring)
      const normalizedWorkSchedule = getSelectableKey(workHoursArrangementOptions, data.workSchedule || '')
      const normalizedBenefits = (data.benefits || []).map((value) => getSelectableKey(benefitsOptions, value))
      const normalizedEducation = (data.education || []).map((value) => getSelectableKey(trainingOpportunitiesOptions, value))
      const normalizedEmployeeLocation = getSelectableKey(countryOptions, data.employeeLocation)
      const payload = {
        position: {
          ...data,
          employeeLocation: normalizedEmployeeLocation,
          workType: normalizedWorkType,
          employmentType: normalizedEmploymentType,
          employmentScope: normalizedEmploymentScope,
          urgentHiring: normalizedUrgentHiring,
          workSchedule: normalizedWorkSchedule,
          showSalaryToTalent: data.showSalaryToTalent ?? true,
          id: '', // Empty ID for new position
          skills: data.skills?.map((x) => ({ name: x.name, matchScore: x.matchScore })),
          benefits: normalizedBenefits,
          education: normalizedEducation,
          physicalConditions: data.physicalConditions || [],
          desiredAttributes1: data.desiredAttributes1 || [],
          desiredAttributes2: data.desiredAttributes2 || [],
          department: ''
        },
        departmentId: data.departmentId
      }
      console.log('🚀 Creating position with payload:', payload)
      dispatch(createPosition(payload)).then(() => handleClose(false))
    } else {
      console.error('Skills and department are required')
    }
  }

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      maxWidth="xl"
      scroll="paper"
      sx={{ minHeight: '90vh', zIndex: 1000 }}>
      <DialogTitle sx={{ boxShadow: 3 }}>Create New Job Position</DialogTitle>
      <DialogContent>
        {/* Department Required check removed - allow creating position without pre-selected department */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            justifyContent={'space-between'}>
            <Grid
              container
              spacing={3}
              xs={9}>
              <Grid
                item
                xs={12}
                sm={4}>
                <FormControl
                  fullWidth
                  sx={{ mt: 2 }}>
                  <InputLabel id="department-label">Department</InputLabel>
                  <Controller
                    name="departmentId"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="department-label"
                        label="Department"
                        error={!!errors.departmentId}
                        disabled={!!departmentId}>
                        {loading ? (
                          <MenuItem value="">Loading...</MenuItem>
                        ) : (
                          departments.map((department: Department) => (
                            <MenuItem
                              key={department.id}
                              value={department.id}>
                              {department.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    )}
                  />
                  {errors.departmentId && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.departmentId.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      sx={{ mt: 2 }}
                      label="Unit"
                      {...field}
                      error={!!errors.unit}
                      helperText={errors.unit?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="team"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      sx={{ mt: 2 }}
                      label="Team"
                      {...field}
                      error={!!errors.team}
                      helperText={errors.team?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={7}>
                <Controller
                  name="jobTitle"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={jobTitleOptions}
                      value={field.value ? { title: field.value } : null}
                      inputValue={field.value ?? ''}
                      onInputChange={(inputValue) => {
                        field.onChange(inputValue)
                        handleJobTitleInputDebounced(inputValue)
                      }}
                      onChange={(newValue) => {
                        field.onChange(newValue?.title ?? '')
                      }}
                      name={field.name}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      fullWidth
                      label="Job Title"
                      error={!!errors.jobTitle}
                      helperText={errors.jobTitle?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={5}>
                <FormControl fullWidth>
                  <InputLabel id="seniority-level-label">Seniority Level</InputLabel>
                  <Controller
                    name="seniorityLevel"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="seniority-level-label"
                        defaultValue=""
                        label="Seniority Level"
                        error={!!errors.seniorityLevel}>
                        {seniorityLevels.map((level) => (
                          <MenuItem
                            key={level}
                            value={level}>
                            {getSeniorityLevelLabel(level, 'en')}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.seniorityLevel && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.seniorityLevel.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={watchedCity ? 3 : watchedEmployeeLocation ? 6 : 9}>
                <FormControl fullWidth>
                  <InputLabel id="employee-location-label">Work Location</InputLabel>
                  <Controller
                    name="employeeLocation"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="employee-location-label"
                        defaultValue=""
                        label="Work Location">
                        {countryOptions.map((country) => (
                          <MenuItem
                            key={country.key}
                            value={country.key}>
                            {country.text}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.employeeLocation && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.employeeLocation.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {watchedEmployeeLocation && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <CreateOptionSelect
                        options={cityOptions}
                        value={{ title: field.value || '' }}
                        onInputChange={(value) => {
                          setValue('city', value)
                          handleCityInputDebounced(value)
                        }}
                        onChange={(value) => setValue('city', value?.title ?? '')}
                        fullWidth
                        label="City"
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              {watchedCity && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="State"
                        error={!!errors.state}
                        helperText={errors.state?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              {watchedState && (
                <Grid
                  item
                  xs={12}
                  sm={3}>
                  <Controller
                    name="district"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="District"
                        error={!!errors.district}
                        helperText={errors.district?.message}
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="workType"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={workingMethodOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(workingMethodOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('workType', getSelectableKey(workingMethodOptions, value?.title ?? ''))
                        trigger('workType')
                      }}
                      fullWidth
                      label="Work Type"
                      error={!!errors.workType}
                      helperText={errors.workType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="employmentType"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={typeOfPositionOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(typeOfPositionOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('employmentType', getSelectableKey(typeOfPositionOptions, value?.title ?? ''))
                        trigger('employmentType')
                      }}
                      fullWidth
                      label="Employment Type"
                      error={!!errors.employmentType}
                      helperText={errors.employmentType?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="employmentScope"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={employmentScopeOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(employmentScopeOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('employmentScope', getSelectableKey(employmentScopeOptions, value?.title ?? ''))
                        trigger('employmentScope')
                      }}
                      fullWidth
                      label="Employment Scope"
                      error={!!errors.employmentScope}
                      helperText={errors.employmentScope?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="visa-sponsorship-label">Do you provide visa & sponsorship for this position?</InputLabel>
                  <Controller
                    name="visaSponsorship"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="visa-sponsorship-label"
                        label="Do you provide visa & sponsorship for this position?">
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.visaSponsorship && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.visaSponsorship.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}>
                <Controller
                  name="urgentHiring"
                  control={control}
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={hiringUrgencyOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(hiringUrgencyOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('urgentHiring', getSelectableKey(hiringUrgencyOptions, value?.title ?? ''))
                        trigger('urgentHiring')
                      }}
                      fullWidth
                      label="How Urgent Is Your Hiring Need?"
                      error={!!errors.urgentHiring}
                      helperText={errors.urgentHiring?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="minSalary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Minimum Salary (Gross/Year)"
                      {...field}
                      error={!!errors.minSalary}
                      helperText={errors.minSalary?.message}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <Controller
                  name="maxSalary"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      label="Maximum Salary (Gross/Year)"
                      {...field}
                      error={!!errors.maxSalary}
                      helperText={errors.maxSalary?.message}
                      InputProps={{
                        inputComponent: NumericFormatCustom as any
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="currency-label">Currency</InputLabel>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="currency-label"
                        label="Currency"
                        error={!!errors.currency}>
                        <MenuItem value="try">Turkish Lira (TL)</MenuItem>
                        <MenuItem value="usd">US Dollar (USD)</MenuItem>
                        <MenuItem value="eur">Euro (EUR)</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.currency && (
                    <Typography
                      variant="caption"
                      color="error">
                      {errors.currency.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="showSalaryToTalent"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                        label={t('positions.form.showSalaryToTalent')}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary">
                        {t('positions.form.showSalaryToTalentHelp')}
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <SkillsSection
                  selectedSkills={selectedSkills}
                  handleAddSkill={handleAddSkill}
                  availableSkills={allSkills}
                  isMatched={allSkills.length > 0}
                  onSkillSearch={handleSkillSearchDebounced}
                />
                {errors.skills && (
                  <Typography
                    variant="caption"
                    color="error">
                    {errors.skills.message}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="desiredAttributes1"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <PositionPointDistribution
                      title={t('positions.form.desiredAttributes1')}
                      options={desiredAttributes1Options}
                      values={(field.value as PositionDistributionValue[]) || []}
                      onChange={(next) =>
                        setValue('desiredAttributes1', next, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      helperText={errors.desiredAttributes1?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="desiredAttributes2"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <PositionPointDistribution
                      title={t('positions.form.desiredAttributes2')}
                      options={desiredAttributes2Options}
                      values={(field.value as PositionDistributionValue[]) || []}
                      onChange={(next) =>
                        setValue('desiredAttributes2', next, {
                          shouldDirty: true,
                          shouldValidate: true
                        })
                      }
                      helperText={errors.desiredAttributes2?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.benefits}>
                  <InputLabel id="benefits-label">{t("positions.form.selectBenefits")}</InputLabel>
                  <Controller
                    name="benefits"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="benefits-label"
                        label={t("positions.form.selectBenefits")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = benefitsOptions.find((opt) => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {benefitsOptions.map((benefit) => (
                          <MenuItem
                            key={benefit.key}
                            value={benefit.key}>
                            <Checkbox checked={field.value?.includes(benefit.key) ?? false} />
                            <ListItemText primary={benefit.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.education}>
                  <InputLabel id="education-label">{t("positions.form.selectTrainingOpportunities")}</InputLabel>
                  <Controller
                    name="education"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="education-label"
                        label={t("positions.form.selectTrainingOpportunities")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = trainingOpportunitiesOptions.find((opt) => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {trainingOpportunitiesOptions.map((education) => (
                          <MenuItem
                            key={education.key}
                            value={education.key}>
                            <Checkbox checked={field.value?.includes(education.key) ?? false} />
                            <ListItemText primary={education.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <FormControl
                  fullWidth
                  error={!!errors.physicalConditions}>
                  <InputLabel id="physical-conditions-label">{t("positions.form.selectPhysicalConditions")}</InputLabel>
                  <Controller
                    name="physicalConditions"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="physical-conditions-label"
                        label={t("positions.form.selectPhysicalConditions")}
                        multiple
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const option = physicalConditionsOptions.find(opt => opt.key === value)
                              return (
                                <Chip
                                  key={value}
                                  label={option ? option.text : value}
                                  sx={{ m: 0.5 }}
                                />
                              )
                            })}
                          </Box>
                        )}>
                        {physicalConditionsOptions.map((option) => (
                          <MenuItem
                            key={option.key}
                            value={option.key}>
                            <Checkbox checked={field.value?.includes(option.key) ?? false} />
                            <ListItemText primary={option.text} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="workSchedule"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <CreateOptionSelect
                      options={workHoursArrangementOptions.map((x) => ({
                        title: x.text
                      }))}
                      value={{ title: getSelectableLabel(workHoursArrangementOptions, field.value || '') }}
                      onChange={(value) => {
                        setValue('workSchedule', getSelectableKey(workHoursArrangementOptions, value?.title ?? ''))
                        trigger('workSchedule')
                      }}
                      fullWidth
                      label={t("positions.form.selectWorkSchedule")}
                      error={!!errors.workSchedule}
                      helperText={errors.workSchedule?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="aboutUs"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label="About Us"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.aboutUs}
                      helperText={errors.aboutUs?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="teamResponsibilities"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label="Team & Responsibilities"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.teamResponsibilities}
                      helperText={errors.teamResponsibilities?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="requirements"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label="Requirements"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.requirements}
                      helperText={errors.requirements?.message}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}>
                <Controller
                  name="whatWeOffer"
                  control={control}
                  render={({ field }) => (
                    <RichTextField
                      fullWidth
                      label="What We Offer"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={!!errors.whatWeOffer}
                      helperText={errors.whatWeOffer?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid
              xs={3}
              sx={{ pt: 2 }}>
              <RoleChipSelect />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2, boxShadow: 3 }}>
        <FormControl sx={{ minWidth: 120 }}>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                labelId="status-label"
                error={!!errors.status}
                size="small"
                sx={{ borderRadius: '50px', display: 'flex' }}>
                <MenuItem value="Posted">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'green' }} />
                  Posted
                </MenuItem>
                <MenuItem value="No Post">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'red' }} />
                  No Post
                </MenuItem>
                <MenuItem value="Draft">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'orange' }} />
                  Draft
                </MenuItem>
                <MenuItem value="Closed">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'gray' }} />
                  Closed
                </MenuItem>
                <MenuItem value="Archived">
                  <CircleIcon sx={{ fontSize: '10px', mr: '7px', color: 'blue' }} />
                  Archived
                </MenuItem>
              </Select>
            )}
          />
          {errors.status && (
            <Typography
              variant="caption"
              color="error">
              {errors.status?.message}
            </Typography>
          )}
        </FormControl>
        <Button
          variant="contained"
          sx={{ bgcolor: 'grey' }}
          onClick={() => handleClose(false)}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default JobCreatePage
